'use client'

import Image from 'next/image'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/utils/styles/cn'
import { LandingVideoThumbnail } from '@/features/landing-page/v3/landing-video-thumbnail'
import { LpDotsIndicator } from '@/features/landing-page/v3/lp-dots-indicator'
import { LP_GRID } from '@/features/landing-page/v3/lp-grid'
import { TESTIMONIALS, type Testimonial } from '@/features/landing-page/v3/sections/testimonials-data'
import { useLandingMediaOverlay } from '@/features/landing-page/landing-media-overlay-context'

// useLayoutEffect on the client, useEffect on the server (avoids the SSR warning).
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * lp-v3 "Loved by customers" testimonial carousel. EVERY testimonial is rendered in the static HTML
 * (stacked, only the active one visible) so all quotes stay crawlable for SEO even though one shows
 * at a time. Changing testimonial cross-fades the content and smoothly animates the card height to
 * fit. Navigation: prev/next authors on desktop, dots everywhere, and swipe on touch devices. The
 * "▶ Play video" thumbnail opens the shared landing media overlay. `!` overrides the `.ds2` defaults.
 */
export function LpTestimonialsCarousel() {
    const [activeIndex, setActiveIndex] = useState(0)
    const [mounted, setMounted] = useState(false)
    const [height, setHeight] = useState<number>()
    const overlay = useLandingMediaOverlay()
    const itemRefs = useRef<(HTMLDivElement | null)[]>([])
    const touchStartX = useRef<number | null>(null)

    const count = TESTIMONIALS.length
    const previous = TESTIMONIALS[(activeIndex - 1 + count) % count]
    const next = TESTIMONIALS[(activeIndex + 1) % count]

    const goTo = (index: number) => setActiveIndex((index + count) % count)

    // Once mounted, the stack is absolutely positioned and its height is driven by the active item
    // (so it can transition). Before mount the active item stays in flow → correct SSR height.
    useIsomorphicLayoutEffect(() => {
        setMounted(true)
    }, [])

    useIsomorphicLayoutEffect(() => {
        const element = itemRefs.current[activeIndex]
        if (element) setHeight(element.offsetHeight)
    }, [activeIndex, mounted])

    // Re-measure when the active item reflows (e.g. viewport resize changes the quote's wrapping).
    useEffect(() => {
        if (!mounted) return
        const element = itemRefs.current[activeIndex]
        if (!element) return
        const observer = new ResizeObserver(() => setHeight(element.offsetHeight))
        observer.observe(element)
        return () => observer.disconnect()
    }, [activeIndex, mounted])

    const playVideo = (testimonial: Testimonial) => {
        if (!testimonial.video) return
        overlay.open({
            items: [
                testimonial.video.kind === 'youtube'
                    ? { type: 'youtube', embedId: testimonial.video.embedId, alt: testimonial.authorName }
                    : { type: 'video', src: testimonial.video.src, alt: testimonial.authorName },
            ],
        })
    }

    const onTouchStart = (event: React.TouchEvent) => {
        touchStartX.current = event.touches[0].clientX
    }
    const onTouchEnd = (event: React.TouchEvent) => {
        if (touchStartX.current === null) return
        const delta = event.changedTouches[0].clientX - touchStartX.current
        touchStartX.current = null
        if (Math.abs(delta) < 40) return
        goTo(activeIndex + (delta < 0 ? 1 : -1))
    }

    return (
        <div
            className={`relative flex w-full flex-col items-center rounded-[24px] border border-[#D7D2C9] bg-transparent px-6 pb-10 pt-10 md:px-8 md:py-16 ${LP_GRID.stageMinH}`}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {/* Height-animated stack — every testimonial stays in the DOM (SEO); only the active one is visible.
                `md:my-auto` makes the quote the elastic middle: it absorbs the stage's height growth equally
                above and below, staying optically centred while the controls pin to the bottom of the card. */}
            <div
                className="relative w-full overflow-hidden transition-[height] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] md:my-auto"
                style={{ height: mounted ? height : undefined }}
            >
                {TESTIMONIALS.map((testimonial, index) => {
                    const isActive = index === activeIndex
                    return (
                        <div
                            key={testimonial.id}
                            ref={(element) => {
                                itemRefs.current[index] = element
                            }}
                            aria-hidden={!isActive}
                            className={cn(
                                'inset-x-0 top-0 flex flex-col items-center text-center transition-opacity duration-300',
                                mounted ? 'absolute' : isActive ? 'relative' : 'absolute',
                                isActive ? 'opacity-100' : 'pointer-events-none opacity-0'
                            )}
                        >
                            {/* Author */}
                            <div className="flex flex-col items-center gap-1">
                                <div className="flex items-center gap-2">
                                    <Image
                                        src={testimonial.avatarUrl}
                                        alt={testimonial.authorName}
                                        width={28}
                                        height={28}
                                        className="size-6 rounded-full object-cover"
                                    />
                                    <span className="text-[14px]! font-medium text-foreground">
                                        {testimonial.authorName}
                                    </span>
                                </div>
                                <span className="text-[11px]! font-medium uppercase tracking-[0.08em] text-muted-foreground">
                                    {testimonial.authorTitle}
                                </span>
                            </div>

                            {/* Quote */}
                            <blockquote className="mx-auto mt-6 max-w-[680px] text-[26px]! font-medium leading-[1.12]! tracking-[-0.01em]! text-foreground md:mt-8 md:text-[40px]!">
                                “{testimonial.quote}”
                            </blockquote>

                            {/* Video thumbnail — HubSpot only; other testimonials are quote-only. */}
                            {testimonial.video && (
                                <div className="mt-8 md:mt-10">
                                    <LandingVideoThumbnail
                                        onClick={() => playVideo(testimonial)}
                                        label="Play video"
                                        ariaLabel={`Play video from ${testimonial.authorName}`}
                                        tabIndex={isActive ? 0 : -1}
                                        className="h-[74px] w-[132px]"
                                    >
                                        {testimonial.video.kind === 'youtube' ? (
                                            // eslint-disable-next-line @next/next/no-img-element -- external YouTube thumbnail, no next/image domain config
                                            <img
                                                src={testimonial.posterUrl}
                                                alt=""
                                                loading="lazy"
                                                className="absolute inset-0 size-full object-cover"
                                            />
                                        ) : (
                                            <video
                                                src={testimonial.video.src}
                                                muted
                                                playsInline
                                                preload="metadata"
                                                className="absolute inset-0 size-full object-cover"
                                            />
                                        )}
                                    </LandingVideoThumbnail>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Controls: prev / dots / next (shared; pinned to the bottom of the card — the quote above
                takes the elastic middle, so the nav no longer floats in the vertical centre) */}
            <div className="mt-8 flex w-full items-center justify-between gap-4 md:mt-12">
                <button
                    type="button"
                    onClick={() => goTo(activeIndex - 1)}
                    aria-label={`Previous testimonial: ${previous.authorName}`}
                    className="group hidden min-w-0 flex-1 items-center gap-2 md:flex"
                >
                    <ArrowLeft className="size-4 shrink-0 text-foreground/40 transition-colors group-hover:text-foreground" />
                    <span className="truncate text-[16px]! font-normal leading-[1.25]! tracking-[-0.02em]! text-foreground">
                        {previous.authorName}
                    </span>
                    {/* Company logo only — no company name (name lives in alt for SEO/a11y). */}
                    <Image
                        src={previous.companyLogoUrl ?? previous.avatarUrl}
                        alt={`${previous.company} logo`}
                        width={20}
                        height={20}
                        className="size-5 shrink-0 rounded-[6px] object-cover"
                    />
                </button>

                <div className="flex flex-1 justify-center">
                    <LpDotsIndicator
                        count={TESTIMONIALS.length}
                        activeIndex={activeIndex}
                        onSelect={goTo}
                        getLabel={(index) => `Show testimonial from ${TESTIMONIALS[index].authorName}`}
                    />
                </div>

                <button
                    type="button"
                    onClick={() => goTo(activeIndex + 1)}
                    aria-label={`Next testimonial: ${next.authorName}`}
                    className="group hidden min-w-0 flex-1 items-center justify-end gap-2 md:flex"
                >
                    {/* Company logo only — no company name (name lives in alt for SEO/a11y). */}
                    <Image
                        src={next.companyLogoUrl ?? next.avatarUrl}
                        alt={`${next.company} logo`}
                        width={20}
                        height={20}
                        className="size-5 shrink-0 rounded-[6px] object-cover"
                    />
                    <span className="truncate text-[16px]! font-normal leading-[1.25]! tracking-[-0.02em]! text-foreground">
                        {next.authorName}
                    </span>
                    <ArrowRight className="size-4 shrink-0 text-foreground/40 transition-colors group-hover:text-foreground" />
                </button>
            </div>
        </div>
    )
}
